import React, { useContext, useEffect, useState } from 'react';
import { Link, navigate } from 'gatsby';
import { getUtms } from 'utils/analytics';
import handleTrailingSlash from '../../../../shared/base/utils/handleTrailingSlash';
import getSplitUrlExperiment from '../../../utils/getSplitUrlExperiment';
import getIfSimpleRedirect from '../../../utils/getIfSimpleRedirect';
import { BASContext } from '../../../providers/basContext';
import { StyledInternalCtaLink, StyledExternalCtaLink } from './styles';

const LinkComponent = ({
  title,
  url,
  isButton,
  hasBackground,
  customButtonBackgroundColor,
  customButtonTextColor,
  useBoldButtonText,
  disableUtmParams,
  children,
  activeClassName,
  className,
  gtmTrackingInfo,
  gtmEvent,
}) => {
  const isInternal = typeof window !== 'undefined' && (url.includes(window.location.host) || /^\/(?!\/)/.test(url)); // check if link is internal if window is defined

  const rel = !isInternal ? 'noopener noreferrer' : null;
  const ifItsARedirect = !!getIfSimpleRedirect(url) || !!getSplitUrlExperiment(url);
  const target = !isInternal && !ifItsARedirect ? '_blank' : '_self';

  // GTM tracking clicked buttons
  const { gtmElementId, gtmClassName } = gtmTrackingInfo || {};
  const [utmParamsString, setUtmParamsString] = useState('');
  const redirectsToBooking = url && url.includes('https://book.drsmile.de/');

  useEffect(() => {
    if (redirectsToBooking && !disableUtmParams) {
      const utmParams = getUtms();
      const urlContainsQueryPattern = new RegExp(/\?.+=.*/g);
      const string = Object.entries(utmParams).reduce((acc, [key, value]) => {
        if (acc.length === 0 && urlContainsQueryPattern.test(url)) return `?${key}=${value}`;
        else return acc + `&${key}=${value}`;
      }, '');

      setUtmParamsString(string);
    }
  }, [redirectsToBooking]);

  if (ifItsARedirect || !isInternal) {
    if (isButton) {
      if (hasBackground)
        return (
          <StyledExternalCtaLink
            href={`${url}${utmParamsString}`}
            aria-label={title}
            target={target}
            rel={rel}
            className={`primary-link ${className}`}
            customButtonBackgroundColor={customButtonBackgroundColor}
            customButtonTextColor={customButtonTextColor}
            useBoldButtonText={useBoldButtonText}
          >
            {children}
            {title}
          </StyledExternalCtaLink>
        );

      return (
        <a
          href={`${url}${utmParamsString}`}
          aria-label={title}
          target={target}
          rel={rel}
          className={`outlined-link ${className}`}
        >
          {children}
          {title}
        </a>
      );
    }
    return (
      <a href={`${url}${utmParamsString}`} aria-label={title} target={target} rel={rel} className={className}>
        {children}
        {title}
      </a>
    );
  } else {
    const hasParams = url.includes('/?');
    const linkUrl = hasParams ? url : handleTrailingSlash(url);
    const buildQueryString = useContext(BASContext);
    const urlWithPersistentParams =
      process.env.GATSBY_PERSIST_UTM === 'true' && buildQueryString ? `${linkUrl}?${buildQueryString}` : linkUrl;

    const handleClick = event => {
      event.preventDefault();
      return navigate(urlWithPersistentParams, { state: gtmEvent });
    };

    if (isButton) {
      if (hasBackground)
        return (
          <StyledInternalCtaLink
            id={gtmElementId}
            to={linkUrl}
            state={gtmEvent}
            aria-label={title}
            className={`primary-link ${gtmClassName || ''} ${className || ''}`}
            activeClassName={activeClassName}
            target={target}
            rel={rel}
            customButtonBackgroundColor={customButtonBackgroundColor}
            customButtonTextColor={customButtonTextColor}
            useBoldButtonText={useBoldButtonText}
          >
            {children}
            {title}
          </StyledInternalCtaLink>
        );

      return (
        <Link
          id={gtmElementId}
          to={linkUrl}
          state={gtmEvent}
          aria-label={title}
          className={`outlined-link ${gtmClassName || ''} ${className || ''}`}
          activeClassName={activeClassName}
          target={target}
          rel={rel}
        >
          {children}
          {title}
        </Link>
      );
    }
    return isInternal && process.env.GATSBY_PERSIST_UTM === 'true' ? (
      <Link
        id={gtmElementId}
        to={linkUrl}
        state={gtmEvent}
        aria-label={title}
        activeClassName={activeClassName}
        target={target}
        rel={rel}
        onClick={handleClick}
        {...(className || gtmClassName ? { className: `${gtmClassName || ''} ${className || ''}` } : null)}
      >
        {children}
        {title}
      </Link>
    ) : (
      <Link
        id={gtmElementId}
        to={linkUrl}
        state={gtmEvent}
        aria-label={title}
        activeClassName={activeClassName}
        target={target}
        rel={rel}
        {...(className || gtmClassName ? { className: `${gtmClassName || ''} ${className || ''}` } : null)}
      >
        {children}
        {title}
      </Link>
    );
  }
};

export default LinkComponent;
